import { css, SerializedStyles } from "@emotion/react";
import styled from "@emotion/styled";

import { varColor } from "../../utils/themeHelpers";

type TypographyType =
    | "display"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "list"
    | "label"
    | "minimum"
    | "number"
    | "toggle"
    | "body";

export interface ITypographyProps {
    type: TypographyType;
    uppercase?: boolean;
    display?: "span" | "block" | "inline-block";
    color?: string;
    horizontalAlign?: "left" | "center" | "right";
    selectable?: boolean;
}

const defaultTypographyColors = {
    display: varColor("primary", "white"),
    toggle: varColor("primary", "white"),
    h1: varColor("primary", "white"),
    h2: varColor("primary", "white"),
    h3: varColor("primary", "white"),
    h4: varColor("primary", "white"),
    list: varColor("primary", "silver"),
    label: varColor("primary", "silver"),
    minimum: varColor("primary", "silver"),
    number: varColor("primary", "silver"),
    body: varColor("primary", "silver"),
} satisfies {
    [key in TypographyType]: string;
};

export const typographyStyles = {
    display: css`
        font-style: normal;
        font-weight: 300;
        font-size: 2.5rem;
        line-height: 4.8rem;
    `,
    h1: css`
        font-style: normal;
        font-weight: normal;
        font-size: 1.75rem;
        line-height: 2.5rem;
        letter-spacing: 0;
    `,
    h2: css`
        font-style: normal;
        font-weight: normal;
        font-size: 1.25rem;
        line-height: 1.875rem;
        letter-spacing: 0.06875rem;
        font-feature-settings: "zero" on;
    `,
    h3: css`
        font-style: normal;
        font-weight: 500;
        font-size: 1.125rem;
        line-height: 2.7rem;
        letter-spacing: 0.06875rem;
    `,
    h4: css`
        font-style: normal;
        font-weight: 700;
        font-size: 0.8125rem;
        line-height: 2.2rem;
        letter-spacing: 0.06875rem;
        text-transform: uppercase;
    `,
    list: css`
        font-style: normal;
        font-weight: normal;
        font-size: 0.8125rem;
        line-height: 1.25rem;
        letter-spacing: 0.0375rem;
    `,
    toggle: css`
        // from https://www.figma.com/design/hq5EPEwggNb52wPXRgqTIh/UI-platform---Real-time-connection?node-id=1455-137381&t=dRSXuYm72DKTjyOA-0
        font-style: normal;
        font-weight: 400;
        font-size: 13px;
        line-height: 20px;
        letter-spacing: 0.6px;
    `,
    label: css`
        font-style: normal;
        font-weight: 700;
        font-size: 0.625rem;
        line-height: 1.125rem;
        letter-spacing: 0.06875rem;
        text-transform: uppercase;
    `,
    minimum: css`
        font-style: normal;
        font-weight: normal;
        font-size: 0.625rem;
        line-height: 0.9375rem;
        letter-spacing: 0.0625rem;
    `,
    number: css`
        font-variant-numeric: tabular-nums;
    `,
    body: css`
        font-style: normal;
        font-weight: normal;
        font-size: 1rem;
        line-height: 2.7rem;
        letter-spacing: 0.05625rem;
        font-feature-settings: "zero" on;
    `,
} satisfies { [key in TypographyType]: SerializedStyles };

export const typographyCoreStyles = css`
    font-family: Moderat, sans-serif;
`;

const Typography = styled.span<ITypographyProps>(
    typographyCoreStyles,
    props => typographyStyles[props.type],
    props =>
        props.display &&
        props.display !== "span" &&
        css`
            display: ${props.display};
        `,
    props =>
        props.uppercase &&
        css`
            text-transform: uppercase;
        `,
    props =>
        props.horizontalAlign &&
        css`
            text-align: ${props.horizontalAlign};
        `,
    props => ({ color: props.color ?? defaultTypographyColors[props.type] }),
    props =>
        !(props.selectable ?? false) &&
        css`
            -webkit-touch-callout: none;
            user-select: none;
        `,
);

Typography.displayName = "Typography";

export { Typography };
